import { Button, Menu, MenuDivider, MenuItem, MenuList, Tooltip } from '@lokalise/louis';
import type { FC } from 'react';
import { useState } from 'react';

import { scheduleToast } from '@app/components/Toast';
import commentsActions from '@app/features/comments/store/actions';
import useDispatch from '@app/hooks/useDispatch';
import { useFeatureState } from '@app/hooks/useFeatureState';
import type { Branch } from '@app/reducers';
import type { RootState } from '@app/types';

import BranchCreateModal from '../BranchCreateModal';
import BranchSearchInput from './BranchSearchInput';
import { BranchContainer, BranchNameText, Container, DownIcon, StyledSelectedIcon } from './styles';

type BranchList = Pick<Branch, 'name'>[];

type BranchSelectProps = {
    projectId: string;
    currentBranchName: string;
    hasAdminRightsSettings: boolean;
    hasAdminRightsCreateBranch: boolean;
    hasAdminRightsMergeBranch: boolean;
    // We only need branch name in this component
    branches: Omit<RootState['branch'], 'list' | 'branchId'> & { list: BranchList };
    onSelected: (branchName: string) => void;
    isMasterOnlyPage?: boolean;
};

const BranchSelect: FC<BranchSelectProps> = ({
    projectId,
    currentBranchName,
    hasAdminRightsSettings,
    hasAdminRightsCreateBranch,
    hasAdminRightsMergeBranch,
    branches,
    onSelected,
    isMasterOnlyPage,
}) => {
    const [modalOpen, setModalOpen] = useState(false);
    const dispatch = useDispatch();
    const [filterValue, setFilterValue] = useState('');
    const isGranularPermissionsEnabled = useFeatureState('homeFeatureGranularPermissions');

    const branchList = [{ name: 'master' }, ...branches.list];
    const filteredBranchList = branchList.filter(
        ({ name }) => filterValue.trim() === '' || name.toLowerCase().includes(filterValue),
    );

    const handleFilterChange = (value: string) => {
        setFilterValue(value.toLowerCase());
    };

    const branchesItems = () => {
        if (!isGranularPermissionsEnabled) {
            return (
                <>
                    {(hasAdminRightsCreateBranch || hasAdminRightsSettings) && <MenuDivider />}
                    {hasAdminRightsCreateBranch && (
                        <MenuItem aria-label="Create new branch" onClick={() => setModalOpen(true)}>
                            Create new branch
                        </MenuItem>
                    )}
                    {hasAdminRightsSettings && (
                        <MenuItem aria-label="Manage branches" href={`/settings/${projectId}/#branches`}>
                            Manage branches
                        </MenuItem>
                    )}
                </>
            );
        }

        return (
            <>
                {(hasAdminRightsMergeBranch || hasAdminRightsCreateBranch) && <MenuDivider />}
                {hasAdminRightsCreateBranch && (
                    <MenuItem aria-label="Create new branch" onClick={() => setModalOpen(true)}>
                        Create new branch
                    </MenuItem>
                )}
                {hasAdminRightsMergeBranch && (
                    <MenuItem aria-label="Manage branches" href={`/settings/${projectId}/#branches`}>
                        Manage branches
                    </MenuItem>
                )}
            </>
        );
    };

    if (isMasterOnlyPage) {
        return (
            <Tooltip tooltip="At the moment Workflows only work with Master branch, so we have switched your working branch to Master">
                <Button size="xs" disabled>
                    master
                </Button>
            </Tooltip>
        );
    }

    return branches.branchToggle ? (
        <>
            <Menu
                menuButton={({ opened }) => (
                    <Button active={opened} size="xs" variant="primary" rightIcon={<DownIcon $opened={opened} />}>
                        <BranchNameText>{branches.branchName || 'master'}</BranchNameText>
                    </Button>
                )}
            >
                <MenuList>
                    <Container>
                        {branchList.length >= 7 && <BranchSearchInput onValueChange={handleFilterChange} />}
                        <BranchContainer>
                            {filteredBranchList.length > 0 ? (
                                filteredBranchList.map(({ name }) => (
                                    <MenuItem key={name} onClick={() => onSelected(name)}>
                                        {currentBranchName === name && <StyledSelectedIcon ariaLabel="Selected" />}
                                        {name}
                                    </MenuItem>
                                ))
                            ) : (
                                <MenuItem disabled>No branches found</MenuItem>
                            )}
                        </BranchContainer>
                        {branchesItems()}
                    </Container>
                </MenuList>
            </Menu>
            {modalOpen && (
                <BranchCreateModal
                    projectId={projectId}
                    opened={modalOpen}
                    onClose={() => setModalOpen(false)}
                    onBranchCreated={(branchName) => {
                        dispatch(commentsActions.closeSidebar());

                        scheduleToast({
                            type: 'info',
                            title: `Branch ${branchName} successfully created.`,
                            description: `We're copying the keys to the new branch. This can take some time depending on the number of keys.`,
                        });

                        window.location.href = `/project/${projectId}/?view=&branch=${branchName}&search=`;
                    }}
                />
            )}
        </>
    ) : (
        <Button size="xs" disabled>
            All branches
        </Button>
    );
};

export default BranchSelect;
